import { QueryCache, QueryClient, MutationCache } from '@tanstack/react-query';

import { showGrowl } from '../components/common/Growl';
import { mapHttpError } from '../utils/httpErrorMessage';

const STALE_TIME_MS = 30_000;

/**
 * Minimum milliseconds that must pass before the same notification title is
 * shown again. Prevents toast storms when many queries fail simultaneously.
 */
const GROWL_THROTTLE_WINDOW_MS = 4_000;

/** Module-level map: title → last-shown timestamp (ms). */
const growlLastShownMs = new Map<string, number>();

/**
 * Show a growl notification, but suppress identical titles fired within
 * GROWL_THROTTLE_WINDOW_MS of each other.
 */
const showGrowlThrottled = (
  payload: NonNullable<ReturnType<typeof mapHttpError>>,
): void => {
  const now = Date.now();
  const lastShown = growlLastShownMs.get(payload.title) ?? 0;
  if (now - lastShown < GROWL_THROTTLE_WINDOW_MS) return;
  growlLastShownMs.set(payload.title, now);
  showGrowl(payload);
};

const handleCacheError = (error: unknown): void => {
  const mapped = mapHttpError(error);
  if (mapped !== null) {
    showGrowlThrottled(mapped);
  }
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleCacheError,
  }),
  mutationCache: new MutationCache({
    onError: handleCacheError,
  }),
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME_MS,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});
